import React from 'react';

import {
	extractInstruction,
	type Instruction,
} from '@atlaskit/navigation-system/side-nav-items/drag-and-drop/hitbox';
import { MenuList } from '@atlaskit/navigation-system/side-nav-items/menu-list';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { L1_MENU_GROUPS } from '@atlassian/navigation-common';
import {
	createElementRegistry,
	ElementRegistryProvider,
	GroupDropIndicatorWithDragOver,
	isL1ItemData,
	isL1ItemDataInGroup,
	isMoreMenuData,
	useL1DragState,
} from '@atlassian/navigation-drag-and-drop';

import type { GlobalSideNavigationProps } from '../../common/types';
import NavItem from '../../common/ui/nav-item';
import { useNavAnalytics } from '../../common/utils/use-nav-analytics';
import {
	type ItemsAndShortcutsProviderProps,
	useItemsAndShortcuts,
} from '../../controllers/items-and-shortcuts-provider';
import { type OptionsProviderProps, useOptions } from '../../controllers/options-provider';
import { useDragAndDropSave } from '../../controllers/use-drag-and-drop-save';
import { useUserMenuDataShapes } from '../../controllers/user-state/user-menu-data-shapes-provider';
import { GLOBAL_SIDE_NAV_TEST_ID } from '../constants';
import { GSNSideNavigationLayout } from '../layout/gsn-layout';
import { PostMainItems } from '../post-main-items';

export type SideNavProps = Omit<
	GlobalSideNavigationProps,
	keyof OptionsProviderProps | keyof ItemsAndShortcutsProviderProps | 'peekMenuId'
>;

export function SideNav(props: SideNavProps) {
	const { bottomElem, postItemsElem, postMainElem, topElem, openShortcutsInSameTab } = props;

	const { l1DragAndDropEnabled } = useOptions();
	const { allItems } = useItemsAndShortcuts();
	const userMenuDataShapes = useUserMenuDataShapes();
	const { fireViewedSideNavEvent } = useNavAnalytics();

	// #region Drag and drop
	// Element registry for L1 items
	const [elementRegistry] = React.useState(createElementRegistry);
	const { dragAndDropSave } = useDragAndDropSave(userMenuDataShapes, elementRegistry);

	const [draggingL1Group, setDraggingL1Group] = React.useState<string | null>(null);

	React.useEffect(() => {
		return monitorForElements({
			canMonitor: ({ source }) =>
				isL1ItemData(source.data) &&
				isL1ItemDataInGroup(source.data, [L1_MENU_GROUPS.MENU, L1_MENU_GROUPS.SHORTCUTS]),
			onDragStart({ source }) {
				if (!isL1ItemData(source.data)) {
					return;
				}
				setDraggingL1Group(source.data.group);
			},
			onDrop({ source, location }) {
				const dragging = source.data;
				setDraggingL1Group(null);

				const [innerMost] = location.current.dropTargets;

				if (!innerMost) {
					return;
				}
				const dropTargetData = innerMost.data;

				const instruction: Instruction | null = extractInstruction(dropTargetData);
				if (!instruction) {
					return;
				}

				// Dragging Out from More Menu
				if (
					isL1ItemData(dragging) &&
					isL1ItemData(dropTargetData) &&
					userMenuDataShapes.hiddenMenuIds.includes(dragging.menuItemId)
				) {
					dragAndDropSave({
						type: 'item-unhide',
						draggedMenuItemId: dragging.menuItemId,
						draggedMenuItemLabel: dragging.label,
						droppedOnMenuItemId: dropTargetData.menuItemId,
						instruction,
						trigger: 'pointer',
					});
					return;
				}

				// Drag and Drop Order
				if (
					isL1ItemData(dragging) &&
					isL1ItemData(dropTargetData) &&
					isL1ItemDataInGroup(dragging, L1_MENU_GROUPS.SHORTCUTS) &&
					isL1ItemDataInGroup(dropTargetData, L1_MENU_GROUPS.SHORTCUTS)
				) {
					dragAndDropSave({
						type: 'item-reorder',
						draggedMenuItemId: dragging.menuItemId,
						draggedMenuItemLabel: dragging.label,
						droppedOnMenuItemId: dropTargetData.menuItemId,
						instruction,
						trigger: 'pointer',
					});
					return;
				}
				// Drag and Drop Order within Menu Group
				if (
					isL1ItemData(dragging) &&
					isL1ItemData(dropTargetData) &&
					isL1ItemDataInGroup(dragging, L1_MENU_GROUPS.MENU) &&
					isL1ItemDataInGroup(dropTargetData, L1_MENU_GROUPS.MENU)
				) {
					dragAndDropSave({
						type: 'item-reorder',
						draggedMenuItemId: dragging.menuItemId,
						draggedMenuItemLabel: dragging.label,
						droppedOnMenuItemId: dropTargetData.menuItemId,
						instruction,
						trigger: 'pointer',
					});
					return;
				}

				// Drag into more menu
				if (
					isL1ItemData(dragging) &&
					isL1ItemDataInGroup(dragging, [L1_MENU_GROUPS.MENU, L1_MENU_GROUPS.SHORTCUTS]) &&
					isMoreMenuData(dropTargetData)
				) {
					dragAndDropSave({
						type: userMenuDataShapes.hiddenMenuIds.includes(dragging.menuItemId)
							? 'item-visible'
							: 'item-hide',
						draggedMenuItemId: dragging.menuItemId,
						draggedMenuItemLabel: dragging.label,
						instruction,
						trigger: 'pointer',
					});
					return;
				}
			},
		});
	}, [dragAndDropSave, userMenuDataShapes.hiddenMenuIds]);

	const { groupDropIndicatorRef } = useL1DragState({ l1DragAndDropEnabled });
	// #endregion

	// #region Core rendering
	const mainNavItems = React.useMemo(
		() =>
			userMenuDataShapes.visibleItemMenuIds.map((id) => (
				<NavItem key={id} id={id} item={allItems[id]} />
			)),
		[userMenuDataShapes.visibleItemMenuIds, allItems],
	);

	React.useLayoutEffect(() => {
		fireViewedSideNavEvent();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<ElementRegistryProvider registry={elementRegistry}>
			<GSNSideNavigationLayout
				testId={props.testId ?? GLOBAL_SIDE_NAV_TEST_ID}
				preMainElem={topElem}
				postMainElem={
					<PostMainItems
						postItemsElem={postItemsElem}
						postMainElem={postMainElem}
						draggingL1Group={draggingL1Group}
						openShortcutsInSameTab={openShortcutsInSameTab}
					/>
				}
				bottomElem={bottomElem}
			>
				<MenuList>
					<GroupDropIndicatorWithDragOver
						ref={groupDropIndicatorRef}
						isActive={draggingL1Group === L1_MENU_GROUPS.MENU}
					>
						{mainNavItems}
					</GroupDropIndicatorWithDragOver>
				</MenuList>
			</GSNSideNavigationLayout>
		</ElementRegistryProvider>
	);
	// #endregion
}
